/**
 * Scheduled Reports Management Component
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { ScheduledReport } from '../../types/executive';

const REPORT_TYPES = [
  { value: 'inventory_status', label: 'Inventory Status' },
  { value: 'sales_analysis', label: 'Sales Analysis' },
  { value: 'supplier_performance', label: 'Supplier Performance' },
  { value: 'replenishment_orders', label: 'Replenishment Orders' },
];

const SCHEDULE_PRESETS = [
  { value: '0 8 * * *', label: 'Daily at 8 AM' },
  { value: '0 8 * * 1', label: 'Weekly (Mondays at 8 AM)' },
  { value: '0 8 1 * *', label: 'Monthly (1st at 8 AM)' },
  { value: '0 8 1 1,4,7,10 *', label: 'Quarterly (1st at 8 AM)' },
];

// Helper function to format error messages
const formatErrorMessage = (err: any, defaultMessage: string): string => {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) {
    // Handle Pydantic validation errors
    return detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ') || defaultMessage;
  }
  return detail || defaultMessage;
};

const ScheduledReports: React.FC = () => {
  const queryClient = useQueryClient();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    report_name: '',
    report_type: '',
    schedule_cron: '',
    recipients: '',
    export_format: 'csv',
    filters: {},
  });

  // Fetch scheduled reports
  const { data: reports, isLoading } = useQuery<ScheduledReport[]>({
    queryKey: ['scheduledReports'],
    queryFn: async () => {
      const response = await api.getScheduledReports();
      return response.data;
    },
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => api.createScheduledReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
      handleCloseDialog();
    },
    onError: (err: any) => {
      setError(formatErrorMessage(err, 'Failed to create scheduled report'));
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.updateScheduledReport(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
      handleCloseDialog();
    },
    onError: (err: any) => {
      setError(formatErrorMessage(err, 'Failed to update scheduled report'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteScheduledReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
    },
    onError: (err: any) => {
      setError(formatErrorMessage(err, 'Failed to delete scheduled report'));
    },
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: (id: string) => api.toggleScheduledReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledReports'] });
    },
    onError: (err: any) => {
      setError(formatErrorMessage(err, 'Failed to toggle scheduled report'));
    },
  });

  const handleOpenDialog = (report?: ScheduledReport) => {
    if (report) {
      setEditingReport(report);
      setFormData({
        report_name: report.report_name,
        report_type: report.report_type,
        schedule_cron: report.schedule_cron,
        recipients: report.recipients.join(', '),
        export_format: report.export_format,
        filters: {},
      });
    } else {
      setEditingReport(null);
      setFormData({
        report_name: '',
        report_type: '',
        schedule_cron: '',
        recipients: '',
        export_format: 'csv',
        filters: {},
      });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingReport(null);
    setError(null);
  };

  const handleSubmit = () => {
    if (!formData.report_name || !formData.report_type || !formData.schedule_cron || !formData.recipients) {
      setError('Please fill in all required fields');
      return;
    }

    const data = {
      ...formData,
      recipients: formData.recipients.split(',').map((email) => email.trim()),
    };

    if (editingReport) {
      updateMutation.mutate({ id: editingReport.schedule_id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this scheduled report?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggle = (id: string) => {
    toggleMutation.mutate(id);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ScheduleIcon sx={{ fontSize: 40, mr: 2 }} />
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>Scheduled Reports</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Create Scheduled Report
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Report Name</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Schedule</strong></TableCell>
                <TableCell><strong>Recipients</strong></TableCell>
                <TableCell><strong>Format</strong></TableCell>
                <TableCell><strong>Schedule Status</strong></TableCell>
                <TableCell><strong>Last Run</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : reports && reports.length > 0 ? (
                reports.map((report) => (
                  <TableRow key={report.schedule_id} hover>
                    <TableCell>{report.report_name}</TableCell>
                    <TableCell>
                      {REPORT_TYPES.find((t) => t.value === report.report_type)?.label || report.report_type}
                    </TableCell>
                    <TableCell>{report.schedule_cron}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {report.recipients.join(', ')}
                      </Typography>
                    </TableCell>
                    <TableCell>{report.export_format.toUpperCase()}</TableCell>
                    <TableCell>
                      <Chip
                        label={report.is_active ? 'Active' : 'Inactive'}
                        color={report.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {report.last_run ? new Date(report.last_run).toLocaleString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggle(report.schedule_id)}
                          color={report.is_active ? 'success' : 'default'}
                          title={report.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {report.is_active ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(report)}
                          color="primary"
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(report.schedule_id)}
                          color="error"
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No scheduled reports found. Create one to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingReport ? 'Edit Scheduled Report' : 'Create Scheduled Report'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              required
              label="Report Name"
              value={formData.report_name}
              onChange={(e) => setFormData({ ...formData, report_name: e.target.value })}
            />

            <FormControl fullWidth required>
              <InputLabel>Report Type</InputLabel>
              <Select
                value={formData.report_type}
                label="Report Type"
                onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}
              >
                {REPORT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Schedule</InputLabel>
              <Select
                value={formData.schedule_cron}
                label="Schedule"
                onChange={(e) => setFormData({ ...formData, schedule_cron: e.target.value })}
              >
                {SCHEDULE_PRESETS.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Custom Cron Expression (optional)"
              value={formData.schedule_cron}
              onChange={(e) => setFormData({ ...formData, schedule_cron: e.target.value })}
              placeholder="0 8 * * *"
              helperText="Override schedule preset with custom cron expression"
            />

            <TextField
              fullWidth
              required
              label="Recipients"
              value={formData.recipients}
              onChange={(e) => setFormData({ ...formData, recipients: e.target.value })}
              placeholder="email1@example.com, email2@example.com"
              helperText="Comma-separated email addresses"
            />

            <FormControl fullWidth>
              <InputLabel>Export Format</InputLabel>
              <Select
                value={formData.export_format}
                label="Export Format"
                onChange={(e) => setFormData({ ...formData, export_format: e.target.value })}
              >
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={24} />
            ) : editingReport ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledReports;
